import { _js } from '@ifixit/localize';
import * as React from 'react';
import { forwardRef, ReactNode, useImperativeHandle } from 'react';
import { BubbleMenu } from '@tiptap/react';
import {
   FormControl,
   FormLabel,
   Input,
   Button,
   Flex,
   Link,
   Text,
   Divider,
   Switch,
   HStack,
   Spacer
} from '@chakra-ui/react';
import { LinkOut, Pencil, Trash } from '@core-ds/icons/16';
import { TiptapButton } from '../../components/TiptapButton';
import type { Editor, Range } from '@tiptap/core';
import type { Placement } from 'tippy.js';
import { shadow } from '@core-ds/primitives';

declare type LinkEditorState = 'preview' | 'edit';
export type { LinkEditorState };

const linkEditorWidth = {
   base: '350px',
   sm: '350px',
   md: '500px',
   lg: '600px',
};

const linkEditorStyle = {
   bgColor: 'gray.50',
   border: '1px',
   borderColor: 'gray.300',
   borderRadius: 'md',
   padding: '2.5',
   maxW: '90vw',
   boxShadow: shadow[3],
};

const LinkPreview = props => {
   const buttonStyle = {
      marginLeft: 2,
      size: 'md',
   };

   if (!props.editor.isActive('link')) {
      return null;
   }

   return (
      <Flex maxWidth={linkEditorWidth} {...linkEditorStyle}>
         <Button
            as={Link}
            href={props.url}
            leftIcon={<LinkOut color="var(--chakra-colors-gray-500)" />}
            flexGrow={2}
            variant="link"
            isExternal={true}
         >
            <Text fontSize="md" alignSelf="center" color="blue.500" isTruncated>
               {props.url}
            </Text>
         </Button>
         <TiptapButton
            icon={<Pencil />}
            onClick={props.handleEdit}
            title={_js('Edit link')}
            iconButtonStyle={buttonStyle}
         />
         <TiptapButton
            icon={<Trash />}
            onClick={props.handleRemove}
            title={_js('Remove link')}
            iconButtonStyle={buttonStyle}
         />
      </Flex>
   );
};

const LinkEdit = props => {
   const { editor, url, setUrl, text, setText, newTab, setNewTab, handleSave, handleCancel } = props;
   const textRef = React.useRef<HTMLInputElement>();
   const urlRef = React.useRef<HTMLInputElement>();

   React.useEffect(() => {
      const focusText = () => {
         textRef.current?.focus();
         setNewTab(editor.getAttributes('link').target);
      }
      const focusUrl = () => urlRef.current?.focus();
      // If we're editing an existing link, focus the link text and set the newTab switch to previous value
      const focusCallback = url ? focusText : focusUrl;
      setTimeout(focusCallback, 50);
   }, []);

   const buttonStyle = {
      margin: '2',
      marginTop: '4',
      marginRight: '0',
   };

   const handleKeyDown = event => {
      if (event.key !== 'Enter') {
         return;
      }
      event.preventDefault();
      if (url) {
         handleSave();
      }
   };

   const handleChangeUrl = event => {
      setUrl(event.target.value);
   };

   const handleChangeText = event => {
      setText(event.target.value);
   };

   const handleChangeNewTabSwitch = event => {
      setNewTab(!newTab);
   };

   return (
      <FormControl width={linkEditorWidth} {...linkEditorStyle}>
         <FormLabel>{_js('Link text')}</FormLabel>
         <Input
            bgColor="white"
            fontSize="sm"
            value={text}
            onChange={handleChangeText}
            onKeyDown={handleKeyDown}
            ref={textRef}
         />
         <Divider orientation="horizontal" borderStyle="none" />
         <FormLabel>{_js('Link address')}</FormLabel>
         <Input
            bgColor="white"
            fontSize="sm"
            placeholder="https://www.example.com"
            value={url}
            onChange={handleChangeUrl}
            onKeyDown={handleKeyDown}
            ref={urlRef}
            type="url"
         />
         <Divider orientation="horizontal" borderStyle="none" />

         <Flex justify="flex-end" align="center">
            <HStack mt="4">
               <FormLabel htmlFor="newTabSwitch" mb="0">
                  {_js('Open in a new tab')}
               </FormLabel>
               <Divider orientation="vertical" borderStyle="none" size="10px"/>
               <Switch
                  id="newTabSwitch"
                  size="md"
                  onChange={handleChangeNewTabSwitch}
                  isChecked={newTab}
               />
            </HStack>
            <Spacer />
            <Button onClick={handleCancel} {...buttonStyle}>
               {_js('Cancel')}
            </Button>
            <Button
               variant="solid"
               colorScheme="blue"
               onClick={handleSave}
               isDisabled={!url}
               {...buttonStyle}
            >
               {_js('Confirm Link')}
            </Button>
         </Flex>
      </FormControl>
   );
};

export const LinkEditorBubble = forwardRef(function LinkEditorBubble(
   props: { children?: ReactNode; editor: Editor },
   ref
) {
   const { editor } = props;
   const [url, setUrl] = React.useState('');
   const [text, setText] = React.useState('');
   const [newTab, setNewTab] = React.useState(true);
   const [state, setState] = React.useState('preview');
   const [selection, setSelection] = React.useState<Range>({ from: 0, to: 0 });

   const updateSelection = () => {
      editor.commands.extendMarkRange('link');

      const { from, to } = editor.state.selection;
      setSelection({ from, to });

      if (editor.state.selection.empty) {
         setText('');
         return;
      }

      const selectedText = editor.state.doc.textBetween(from, to);
      setText(selectedText);
   };

   useImperativeHandle(ref, () => ({
      show: (state?: LinkEditorState) => {
         if (state) {
            setState(state);
         }
         if (state === 'edit') {
            updateSelection();
         }

         const attrs = editor.getAttributes('link');
         const href = attrs.href || '';
         const target = attrs.target || '_blank';
         setUrl(href);
         setNewTab(target === '_blank');
         return true;
      },
      hide: () => {
         setText('');
         setUrl('');
         setState('preview');
         return true;
      },
   }));

   const handleSave = () => {
      let href = url;
      const urlHasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(href);

      let target = newTab ? '_blank' : '';

      if (href && !urlHasProtocol) {
         href = 'https://' + href;
         setUrl(href);
      }

      const replacementText = text || href;
      const linkContent = {
         type: 'text',
         marks: [{ type: 'link', attrs: { href, target } }],
         text: replacementText,
      };

      editor.chain().focus().insertContentAt(selection, linkContent).run();
      setState('preview');
   };

   const handleRemove = () => {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
   };

   const handleCancel = () => {
      const attrs = editor.getAttributes('link');
      const href = attrs.href || '';
      const target = attrs.target || '_blank';
      if (href) {
         setUrl(href);
      }
      setNewTab(target === '_blank');
      setState('preview');
      editor.commands.focus();
   };

   const handleEdit = () => {
      updateSelection();
      setState('edit');
   };

   const innerProps = {
      editor,
      url,
      text,
      setUrl,
      setText,
      newTab,
      setNewTab,
      handleEdit,
      handleSave,
      handleRemove,
      handleCancel,
   };

   const duration: [number, number] = [100, 0];
   const placement: Placement = 'bottom';

   const tippyOptions = {
      duration,
      maxWidth: 'none',
      moveTransition: 'transform 0.1s ease 0.05s',
      ignoreAttributes: true,
      placement,
   };

   return (
      editor && (
         <BubbleMenu editor={editor} shouldShow={() => true} tippyOptions={tippyOptions}>
            {state == 'preview' ? <LinkPreview {...innerProps} /> : <LinkEdit {...innerProps} />}
         </BubbleMenu>
      )
   );
});
